import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getTasks, getTask, updateTask, updateTaskResources, generateTasks } from '@/api/tasks';
import type { Task, UpdateTaskRequest, UpdateTaskResourcesRequest } from '@/types/task';
import type { TasksResponse } from '@/types/api';

/**
 * Hook to fetch the list of tasks for the current user.
 *
 * When task_creation_status is 'generation_in_progress', automatically
 * polls every 30 seconds to detect when new tasks are ready.
 *
 * Supports optional filtering by Set ID.
 */
export function useTasks(status: string = 'active', setId?: string | null) {
  return useQuery({
    queryKey: ['tasks', { status, setId }],
    queryFn: () =>
      getTasks({
        status,
        set_id: setId || undefined,
      }),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: (query) =>
      query.state.data?.task_creation_status === 'generation_in_progress'
        ? 30000
        : false,
  });
}

/**
 * Hook to fetch a specific task with full details.
 */
export function useTask(taskId: string | undefined) {
  return useQuery({
    queryKey: ['tasks', taskId],
    queryFn: () => getTask(taskId!),
    enabled: !!taskId,
  });
}

/**
 * Hook to update a task's status (complete or ignore).
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: UpdateTaskRequest }) =>
      updateTask(taskId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/**
 * Hook to update a task's resources (inline edits).
 * Uses optimistic updates to immediately reflect changes in the UI.
 */
export function useUpdateTaskResources() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ taskId, data }: { taskId: string; data: UpdateTaskResourcesRequest }) =>
      updateTaskResources(taskId, data),
    onMutate: async ({ taskId, data }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['tasks', taskId] });

      // Snapshot the previous value
      const previousTask = queryClient.getQueryData<Task>(['tasks', taskId]);

      // Optimistically update the task's resources
      if (previousTask) {
        queryClient.setQueryData<Task>(['tasks', taskId], {
          ...previousTask,
          resources: data.resources,
        });
      }

      return { previousTask };
    },
    onError: (_err, { taskId }, context) => {
      // Roll back to the previous value on error
      if (context?.previousTask) {
        queryClient.setQueryData(['tasks', taskId], context.previousTask);
      }
    },
    onSettled: (_data, _error, { taskId }) => {
      // Refetch to ensure we're in sync with the server
      queryClient.invalidateQueries({ queryKey: ['tasks', taskId] });
    },
  });
}

/**
 * Hook to initiate task generation.
 *
 * On success, optimistically updates the task_creation_status to
 * 'generation_in_progress' in the query cache. Polling will then
 * automatically begin (via useTasks refetchInterval) to detect when
 * generation completes.
 *
 * The first poll is effectively delayed because the optimistic update
 * sets the status immediately, and the 30-second refetchInterval provides
 * natural spacing. An additional initial delay of ~60 seconds is achieved
 * by temporarily setting staleTime on the query after mutation.
 */
export function useGenerateTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateTasks,
    onSuccess: () => {
      // Optimistically update the task_creation_status in all task queries
      queryClient.setQueriesData<TasksResponse>(
        { queryKey: ['tasks'] },
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            task_creation_status: 'generation_in_progress',
          };
        },
      );

    },
  });
}
